import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import Utils from '../../../util';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { UnitLocaleEnum } from '../../../models/enums/results/result-units-locale.enum';
import { SensitivityItem } from '../../../models/results/sensitivity/sensitivity-item.model';
import { take } from 'rxjs';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import { LooseObject } from '../../../models/loose-object.model';

@Component({
  selector: 'app-sensitivity-ai-popup',
  templateUrl: './sensitivity-ai-popup.component.html',
  styleUrls: ['./sensitivity-ai-popup.component.scss']
})
export class SensitivityAIPopupComponent implements OnInit, AnessaTranslationInterface {
  trigger?: string;
  expectedY?: string;

  /**
   * The list of parameters that can be selected by the user.
   */
  parameters: string[] = [];
  /**
   * An array that contains the selected media types.
   */
  outputParametersForAI?: string;
  /**
   * The list of inputs that can be selected for the sensitivity analysis.
   */
  inputs: SensitivityItem[] = [];
  inputTranslations: LooseObject = {};
  /**
   * An array that contains the available media type; used to populate the media type dropdown.
   */
  resultTypes: string[] = [];
  /**
   * Determines if the currency units are ready for use.
   */
  currencyUnitsLoaded = false;
  /**
   * The selected result type for the graph data.
   */
  selectedResultForAI?: string;
  /**
   * The selected unit type, not applicable for all results sets for Sensitivity Solver AI Model.
   */
  selectedUnitsTypeForAI = UnitLocaleEnum.METRIC;
  /**
   * An array that contains the available options for the unit type (locale); used to populate
   * the unit type dropdown.
   */
  unitsTypes: DropdownDisplayItem[] = [new DropdownDisplayItem('RESULTS.GENERAL.METRIC', UnitLocaleEnum.METRIC), new DropdownDisplayItem('RESULTS.GENERAL.IMPERIAL', UnitLocaleEnum.IMPERIAL)];
  /**
   * The selected currency unit.
   */
  selectedCurrencyUnitForAI: Unit | null = null;
  currencyUnits: Unit[];
  desiredX: number | null = null;
  desiredUnits: string | null = null;
  xvals: number[] = [];
  vals: number[] = [];
  baseValue: number | null = null;
  baseUnit: string = '';
  actualCurrency: Unit;
  yMax = 0;
  yMin = 0;
  xMax = 0;
  xMin = 0;
  slope = 0.0;
  gradient = 0.0;
  private sensResultData: any;

  constructor(public dialogRef: MatDialogRef<SensitivityAIPopupComponent>, @Inject(MAT_DIALOG_DATA) public data: any, private projectCommService: ProjectCommunicationService, private unitService: UnitService, private toastrService: ConfiguredToastrService, private translationService: AnessaTranslationService) {
    this.xvals = data.xvals;
    this.vals = data.vals;
    this.currencyUnitsLoaded = data.currencyUnitsLoaded;
    this.parameters = data.parameters;
    this.sensResultData = data.sensResultData;
    this.inputTranslations = data.inputTranslations;
    this.inputs = data.inputs;
    this.resultTypes = data.resultTypes;
    this.currencyUnits = data.currencyUnits;
    this.actualCurrency = data.actualCurrency;
  }

  ngOnInit(): void {
  }


  /**
   * Get the baseline value.
   */
  getTableDataForAI(inputName: string) {
    if (this.inputs && this.inputs.length > 0) {
      for (const input of this.inputs) {
        const precision = this.projectCommService.getPrecision();
        const unitDisplay = Unit.parseUnitDisplayName(this.translationService.get(input.unit));
        if (this.inputTranslations[input.id] === inputName) {
          this.baseValue = input.value
            ? Utils.roundToPrecision(input.value, precision)
            : input.value;
          this.baseUnit = unitDisplay;
        }
      }
    }
    return this.baseValue;
  }

  /**
   * Determines if the unit type dropdown should be shown for AI Sensitivity Solver.
   */

  showUnitTypeDropdownForAI(): boolean {
    return this.selectedResultForAI === ResultSetNames.RNG || this.selectedResultForAI === ResultSetNames.BIOGAS;
  }


  /**
   * Determines if the currency type dropdown should be shown for AI Sensitivity Solver.
   */
  showCurrencyUnitsDropdownForAI(): boolean {
    return this.selectedResultForAI === ResultSetNames.NPV;
  }

  /**
   * Get the fallback unit when no list of units is not used.
   * @returns '%' or an <empty string>
   */
  getSingleUnitValue() {
    return this.selectedResultForAI ? '%' : '';
  }

  clearPrediction() {
    this.desiredX = null;
    this.desiredUnits = null;
  }

  onSave() {
    this.processGraphDataForAI();
  }

  /**
   * Convert the provided array of values to the correct unit.
   * @param y an array of numbers that will be converted.
   * @private
   */
  private valuesInCorrectUnitForAI(y: number[]): number[] {
    if (this.selectedUnitsTypeForAI === UnitLocaleEnum.IMPERIAL && (this.selectedResultForAI === ResultSetNames.BIOGAS || this.selectedResultForAI === ResultSetNames.RNG)) {
      const imperialUnit = this.unitService.getUnitById('000000000000000000003813');
      const metricUnit = this.unitService.getUnitById('000000000000000000003812');
      const convertedValues: number[] = [];
      for (const value of y) {
        convertedValues.push(metricUnit.convertToUnit(Utils.deepCopy(value), imperialUnit));
      }
      return convertedValues;
    }
    if (this.selectedResultForAI === ResultSetNames.NPV && this.selectedCurrencyUnitForAI) {
      const convertedValues: number[] = [];
      for (const value of y) {
        convertedValues.push(this.actualCurrency.convertToUnit(Utils.deepCopy(value), this.selectedCurrencyUnitForAI));
      }
      return convertedValues;
    }
    return y;
  }

  /**
   * processes the result data and populates the graph data with the correct data the user selected.
   * @private
   */
  private processGraphDataForAI() {
    this.desiredUnits = null;
    if (this.expectedY === 'Please enter' || this.expectedY == undefined) {
      this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_NUMERICAL_VALUE'));
      return;
    }
    if (this.selectedResultForAI === ResultSetNames.NPV && !this.selectedCurrencyUnitForAI) {
      this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_CURRENCY_CHOSEN'));
      return;
    }

    if (!this.selectedResultForAI) {
      this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_SELECTED_RESULT'));
      return;
    }

    if (!this.outputParametersForAI || this.outputParametersForAI === undefined) {
      this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_VARIABLE_CHOSEN'));
      return;
    }
    if (!this.expectedY) {
      this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_EXPECTED_Y'));
      return;
    }
    if (this.expectedY.includes(',')) {
      this.expectedY = this.expectedY.replaceAll(',', '');
    }
    if (!Utils.isNumeric(this.expectedY)) {
      this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_NUMERICAL_VALUE'));
      return;
    }

    const expectedValueY = Number(this.expectedY);
    const tempGraph = [];
    this.xMax = 0;
    this.xMin = 0;
    this.yMax = 0;
    this.yMin = 0;
    this.vals = [];
    this.xvals = [];
    if (this.sensResultData) {
      if (this.outputParametersForAI || this.selectedResultForAI) {
        const param = this.outputParametersForAI;
        const trace = {
          name: this.inputTranslations[param],
          mode: 'scatter',
          x: this.sensResultData[param][this.selectedResultForAI].x,
          y: this.valuesInCorrectUnitForAI(this.sensResultData[param][this.selectedResultForAI].y)
        };
        tempGraph.push(trace);
        this.xvals.push(...trace.x);
        this.vals.push(...trace.y);
        this.xMax = Math.max(...this.xvals);
        this.xMin = Math.min(...this.xvals);
        this.yMax = Math.max(...this.vals);
        this.yMin = Math.min(...this.vals);
        this.slope = (this.yMax - this.yMin) / (this.xMax - this.xMin);
        this.gradient = this.yMax - (this.slope * this.xMax);
        console.log(this.yMin);
        console.log(this.yMax);


        if (this.yMax === this.yMin) {
          this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_IMPACT_VARIABLE'));
        } else if (this.yMax === Infinity || this.yMin === Infinity) {
          this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_IMPACT_VARIABLE'));
        } else if (expectedValueY > this.yMax || expectedValueY < this.yMin) {
          if ((this.yMax > 0 && this.yMin > 0) || (this.yMax < 0 && this.yMin < 0)) {
            if (Math.abs(this.yMax) > Math.abs(this.yMin)) {
              if (Math.abs(this.yMax - this.yMin) < 1) {
                if (this.selectedResultForAI === ResultSetNames.NPV && this.selectedCurrencyUnitForAI) {
                  this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + this.yMin + ' ' + this.selectedCurrencyUnitForAI.displayName + ' & ' + this.yMax + ' ' + this.selectedCurrencyUnitForAI.displayName + ' for calculations ');
                } else {
                  this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + this.yMin + ' & ' + this.yMax + ' for calculations ');
                }
              } else {
                if (this.selectedResultForAI === ResultSetNames.NPV && this.selectedCurrencyUnitForAI) {
                  this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + Utils.roundPrecisionUp(Number(this.yMin), 3) + ' ' + this.selectedCurrencyUnitForAI.displayName + ' & ' + Utils.roundDown(this.yMax, 3) + ' ' + this.selectedCurrencyUnitForAI.displayName + ' for calculations ');
                } else {
                  this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + Utils.roundPrecisionUp(Number(this.yMin), 3) + ' & ' + Utils.roundDown(this.yMax, 3) + ' for calculations ');
                }
              }
            } else {
              if (Math.abs(this.yMin - this.yMax) < 1) {
                if (this.selectedResultForAI === ResultSetNames.NPV && this.selectedCurrencyUnitForAI) {
                  this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + this.yMin + ' ' + this.selectedCurrencyUnitForAI.displayName + ' & ' + this.yMax + ' ' + this.selectedCurrencyUnitForAI.displayName + ' for calculations ');
                } else {
                  this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + this.yMin + ' & ' + this.yMax + ' for calculations ');
                }
              } else {
                if (this.selectedResultForAI === ResultSetNames.NPV && this.selectedCurrencyUnitForAI) {
                  this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + Utils.roundPrecisionUp(Number(this.yMin), 3) + ' ' + this.selectedCurrencyUnitForAI.displayName + ' & ' + Utils.roundDown(this.yMax, 3) + ' ' + this.selectedCurrencyUnitForAI.displayName + ' for calculations ');
                } else {
                  this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + Utils.roundPrecisionUp(Number(this.yMin), 3) + ' & ' + Utils.roundDown(this.yMax, 3) + ' for calculations ');
                }
              }
            }

          } else {
            if (this.selectedResultForAI === ResultSetNames.NPV && this.selectedCurrencyUnitForAI) {
              this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + Utils.roundPrecisionUp(Number(this.yMin), 3) + ' ' + this.selectedCurrencyUnitForAI.displayName + ' & ' + Utils.roundDown(this.yMax, 3) + ' ' + this.selectedCurrencyUnitForAI.displayName + ' for calculations ');
            } else {
              this.toastrService.error(this.translationService.get('SENSITIVITY.ERRORS.NO_TARGET_AVAILABLE') + Utils.roundPrecisionUp(Number(this.yMin), 3) + ' & ' + Utils.roundDown(this.yMax, 3) + ' for calculations ');
            }
          }


        } else {
          this.projectCommService.getDesiredXValue(this.slope, this.gradient, expectedValueY, this.xvals, this.vals)
            .pipe(take(1))
            .subscribe(res => {
              if (res !== null && Utils.isNumeric(res)) {
                const tableData = this.getTableDataForAI(this.inputTranslations[param])
                if (!tableData) return;

                const roundedResult = Utils.roundToPrecision(Number(res), 2);
                this.desiredX = (1 + (roundedResult / 100)) * tableData;
                const baseUnit = this.baseUnit;
                this.desiredUnits = baseUnit.replaceAll(/(<sub>)|(<\/sub>)|(<sup>)|(<\/sup>)/g, '');
              } else {

                throw Error('invalid response for Sensitivity Solver calculations');

              }
            });
        }
      }

    } else {
      this.toastrService.warning(this.translationService.get('SENSITIVITY.ERRORS.NO_RESULTS_AVAILABLE'));
    }
  }
}


enum ResultSetNames {
  NPV = 'RESULTS.DATA.ECONOMIC_METRICS.NPV',
  IRR = 'RESULTS.DATA.ECONOMIC_METRICS.IRR',
  PBP = 'RESULTS.DATA.ECONOMIC_METRICS.PP',
  BIOGAS = 'PRODUCTS.BIOGAS',
  RNG = 'PRODUCTS.BIOMETHANE',
  ELEC = 'PRODUCTS.ELECTRICITY'
}
